import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Image as ImageIcon, Video as VideoIcon, X, AlertCircle, Loader2, Sparkles } from 'lucide-react'
import { CLOUD_NAME } from '../config/cloudinary'

// NOTA: el texto de esta sección es genérico/placeholder institucional.
// Reemplázalo con el copy oficial de ExpoGardenias cuando esté disponible.
const DESCRIPCION_INSTITUCIONAL =
  'ExpoGardenias Crea y Emprende es el espacio en el que nuestros estudiantes muestran los ' +
  'proyectos de emprendimiento, ciencia y creatividad desarrollados durante el año escolar. ' +
  'Aquí encontrarás fotos y videos de los eventos más recientes de la institución.'

const ExpoGardenias = () => {
  const [photos, setPhotos] = useState([])
  const [videos, setVideos] = useState([])
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'error' | 'unconfigured'
  const [lightboxUrl, setLightboxUrl] = useState(null)

  useEffect(() => {
    if (CLOUD_NAME === 'PENDIENTE_CONFIGURAR') {
      setStatus('unconfigured')
      return
    }

    let cancelled = false

    const cargarGaleria = async () => {
      setStatus('loading')
      try {
        const [photosRes, videosRes] = await Promise.all([
          fetch(`https://res.cloudinary.com/${CLOUD_NAME}/image/list/expogardenias.json`),
          fetch(`https://res.cloudinary.com/${CLOUD_NAME}/video/list/expogardenias.json`)
        ])

        if (!photosRes.ok || !videosRes.ok) {
          throw new Error('Respuesta no exitosa de Cloudinary')
        }

        const photosData = await photosRes.json()
        const videosData = await videosRes.json()

        if (cancelled) return

        setPhotos(photosData.resources || [])
        setVideos(videosData.resources || [])
        setStatus('ready')
      } catch (error) {
        if (!cancelled) {
          console.error('Error cargando galería de ExpoGardenias:', error)
          setStatus('error')
        }
      }
    }

    cargarGaleria()

    return () => {
      cancelled = true
    }
  }, [])

  const buildPhotoUrl = ({ public_id, format }) =>
    `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/f_auto,q_auto,w_800/${public_id}.${format}`

  const buildVideoUrl = ({ public_id }) =>
    `https://res.cloudinary.com/${CLOUD_NAME}/video/upload/f_auto,q_auto/${public_id}.mp4`

  const buildVideoPosterUrl = ({ public_id }) =>
    `https://res.cloudinary.com/${CLOUD_NAME}/video/upload/f_auto,q_auto,so_0/${public_id}.jpg`

  return (
    <div className="pt-32 pb-16 min-h-screen bg-white">
      <div className="container-custom px-4">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-gardenia-red to-gardenia-green shadow-lg mb-6">
            <Sparkles className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gardenia-dark mb-4">
            ExpoGardenias <span className="text-gardenia-red">Crea y Emprende</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            {DESCRIPCION_INSTITUCIONAL}
          </p>
        </div>

        {/* Estado: placeholder sin configurar */}
        {status === 'unconfigured' && (
          <div className="max-w-xl mx-auto text-center bg-gardenia-gold/20 border border-gardenia-gold rounded-xl p-8">
            <AlertCircle className="h-10 w-10 text-gardenia-red mx-auto mb-4" />
            <p className="text-gardenia-dark font-medium">
              La galería aún no está configurada.
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Falta definir el <code className="bg-white px-1 rounded">CLOUD_NAME</code> de Cloudinary
              en <code className="bg-white px-1 rounded">src/config/cloudinary.js</code>.
            </p>
          </div>
        )}

        {/* Estado: cargando */}
        {status === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-10 w-10 animate-spin text-gardenia-red mb-4" />
            <p>Cargando galería...</p>
          </div>
        )}

        {/* Estado: error */}
        {status === 'error' && (
          <div className="max-w-xl mx-auto text-center bg-red-50 border border-red-200 rounded-xl p-8">
            <AlertCircle className="h-10 w-10 text-gardenia-red mx-auto mb-4" />
            <p className="text-gardenia-dark font-medium">
              No pudimos cargar la galería en este momento.
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Verifica tu conexión a internet o inténtalo de nuevo más tarde.
            </p>
          </div>
        )}

        {/* Estado: listo */}
        {status === 'ready' && (
          <div className="space-y-16">
            {/* Fotos */}
            <section>
              <h2 className="flex items-center gap-2 text-2xl font-bold text-gardenia-dark mb-6">
                <ImageIcon className="h-6 w-6 text-gardenia-red" />
                Fotos
              </h2>
              {photos.length === 0 ? (
                <p className="text-gray-500">Todavía no hay fotos publicadas.</p>
              ) : (
                <div className="columns-2 sm:columns-3 md:columns-4 gap-4">
                  {photos.map((photo) => (
                    <button
                      key={photo.public_id}
                      onClick={() => setLightboxUrl(buildPhotoUrl(photo))}
                      className="group block w-full mb-4 break-inside-avoid overflow-hidden rounded-lg shadow-sm hover:shadow-lg transition-all duration-300"
                    >
                      <img
                        src={buildPhotoUrl(photo)}
                        alt="ExpoGardenias"
                        loading="lazy"
                        className="w-full rounded-lg group-hover:scale-105 transition-transform duration-300"
                      />
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Videos */}
            <section>
              <h2 className="flex items-center gap-2 text-2xl font-bold text-gardenia-dark mb-6">
                <VideoIcon className="h-6 w-6 text-gardenia-green" />
                Videos
              </h2>
              {videos.length === 0 ? (
                <p className="text-gray-500">Todavía no hay videos publicados.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {videos.map((video) => (
                    <video
                      key={video.public_id}
                      controls
                      preload="none"
                      poster={buildVideoPosterUrl(video)}
                      className="w-full rounded-lg shadow-sm"
                    >
                      <source src={buildVideoUrl(video)} type="video/mp4" />
                      Tu navegador no soporta la reproducción de video.
                    </video>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* Lightbox simple */}
        {lightboxUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => setLightboxUrl(null)}
          >
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label="Cerrar"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={lightboxUrl}
              alt="ExpoGardenias ampliada"
              className="max-h-[90vh] max-w-full rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Enlace discreto para docentes */}
        <div className="text-center mt-16">
          <Link
            to="/expogardenias/subir"
            className="text-xs text-gray-400 hover:text-gardenia-red transition-colors underline underline-offset-2"
          >
            ¿Eres docente y quieres subir contenido? Ingresa aquí
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ExpoGardenias
